import { useQuery } from "@tanstack/react-query";

import { toast } from "@krea/shared/design-system/utils/toast";

import { LenderApplicationsFilterEnum } from "@/features/common/enums";
import { queryKeyMap } from "@/features/query-client";
import { getTitleAndDescriptionForFilters } from "@/utils";

import { fetchListLenderApplications } from "../../api";

type ResponseType = Record<LenderApplicationsFilterEnum, number>;

const defaultCounts: ResponseType = {
  no_company_loan_bids: 0,
  no_invoice_purchase_bids: 0,
  no_real_estate_bids: 0,
  no_leasing_bids: 0,
  bid_selected: 0,
  has_own_bids: 0,
  new: 0,
  paid_out: 0,
  rejected: 0,
  closed_by_customer: 0,
};

export const useGetLenderApplicationsCountsQuery = (
  lenderCompanyId: number,
) => {
  const getCount = (filter: LenderApplicationsFilterEnum) =>
    fetchListLenderApplications(lenderCompanyId, {
      filter,
      limit: 0,
      offset: 0,
    });

  return useQuery({
    queryKey: queryKeyMap.getLenderApplicationsCounts,
    enabled: !!lenderCompanyId,
    queryFn: async () => {
      const counts = await Promise.all(
        Object.values(LenderApplicationsFilterEnum).map(async (filter) => {
          try {
            const response = await getCount(filter);

            return { filter, count: response.count };
          } catch (error) {
            toast.error(
              `Failed fetching count for ${getTitleAndDescriptionForFilters(filter).title.toLowerCase()}`,
            );

            throw error;
          }
        }),
      );

      return counts.reduce<ResponseType>(
        (acc, curr) => {
          acc[curr.filter] = curr.count;

          return acc;
        },
        { ...defaultCounts },
      );
    },
  });
};
